import { api } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type { CashCatalogOption, CashCatalogSlug } from '../../cash-division/cashCatalogTypes';
import type {
  DisbursementRequestDetail,
  DisbursementRequestItemInput,
  DisbursementRequestListItem,
  DisbursementRequestListQuery,
} from '../../cash-division/disbursementRequestTypes';

/**
 * The Self Service form. There is no requesting party on it — the server derives it from the
 * signed-in account's own employee record.
 */
export interface MyDisbursementRequestRequest {
  transactionTypeId: string | null;
  paymentTypeId: string | null;
  items: DisbursementRequestItemInput[];
  notes?: string | null;
}

/** The account's own employee record. `error` is set — and the rest omitted — when there is none. */
export interface CashRequester {
  personnelId?: string | null;
  employeeNumber?: string | null;
  /** Every employee record linked to the account, inactive ones included — the server's own set. */
  personnelIds?: string[];
  error?: string | null;
}

function toQueryString(query: DisbursementRequestListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Sent whenever the key is present, empty string included — "ticked to nothing" matches no row.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.transactionType !== undefined) params.set('transactionType', query.transactionType);
  if (query.paymentType !== undefined) params.set('paymentType', query.paymentType);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * The employee's own requests. The scope is fixed by the ROUTE on the server — nothing sent from
 * here can widen it.
 */
const BASE = '/api/self-service/disbursement-requests';

export const myDisbursementRequestsApi = {
  list: (query: DisbursementRequestListQuery, signal?: AbortSignal) =>
    api<PagedResult<DisbursementRequestListItem>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<DisbursementRequestDetail>(`${BASE}/${id}`, { signal }),

  me: (signal?: AbortSignal) => api<CashRequester>(`${BASE}/me`, { signal }),

  /** The form's pickers — served here so an employee needs no Cash Division settings key. */
  catalog: (catalog: CashCatalogSlug, signal?: AbortSignal) =>
    api<CashCatalogOption[]>(`${BASE}/catalogs/${catalog}`, { signal }),

  create: (body: MyDisbursementRequestRequest) =>
    api<DisbursementRequestDetail>(BASE, { method: 'POST', body }),

  update: (id: string, body: MyDisbursementRequestRequest) =>
    api<DisbursementRequestDetail>(`${BASE}/${id}`, { method: 'PUT', body }),
};
